import Chip from '@mui/material/Chip';
import type { FC, ReactElement, ReactNode } from 'react';
import React from 'react';

interface ChipProps {
  id: string;
  variant?: 'filled' | 'outlined';
  color?: MUIChipColor | CustomChipColor;
  label: ReactNode;
  className?: string;
  disabled?: boolean;
  clickable?: boolean;
  icon?: ReactElement;
  onDelete?: () => void;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  deleteIcon?: ReactElement;
  size?: 'medium' | 'small';
  avatar?: ReactElement;
  iconPosition?: 'left' | 'right'; // New prop to position the icon
}

// Define MUI's default colors
type MUIChipColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning';

// Define custom colors
export type CustomChipColor =
  | 'bg-New'
  | 'bg-InProgress'
  | 'bg-CallStarted'
  | 'bg-CallCompleted'
  | 'bg-Completed'
  | 'bg-Cancelled'
  | 'bg-Onhold';

// Custom background colors mapping
const bgColors: Record<CustomChipColor, string> = {
  'bg-New': '#8AA3CA',
  'bg-InProgress': '#F2D398',
  'bg-CallStarted': '#AFC9C1',
  'bg-CallCompleted': '#BBB5CD',
  'bg-Completed': '#CDD2DD',
  'bg-Cancelled': '#F2C3CC',
  'bg-Onhold': '#FFEEE9',
};

const CustomChip: FC<ChipProps> = ({
  id,
  className,
  variant = 'filled',
  color = 'default',
  label,
  disabled,
  clickable,
  icon,
  onDelete,
  onClick,
  deleteIcon,
  size = 'medium',
  avatar,
  iconPosition = 'right', // Default icon position
  ...rest
}) => {
  const customStyles =
    (color as CustomChipColor) in bgColors
      ? { backgroundColor: bgColors[color as CustomChipColor] }
      : {};

  return (
    <Chip
      id={id}
      variant={variant}
      color={
        (color as CustomChipColor) in bgColors
          ? undefined
          : (color as MUIChipColor)
      }
      label={
        icon && iconPosition === 'right' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {label} {icon}
          </span>
        ) : (
          label
        )
      }
      disabled={disabled}
      clickable={clickable}
      icon={icon && iconPosition === 'left' ? icon : undefined} // Set left icon only if applicable
      onDelete={onDelete}
      onClick={onClick}
      size={size}
      className={className}
      avatar={avatar}
      deleteIcon={deleteIcon}
      sx={customStyles}
      {...rest}
    />
  );
};

export default CustomChip;
